import React, { useEffect, useRef } from 'react';
import { Transition } from '@headlessui/react';

import { Icon, Overlay } from 'ui';
import Loader from 'ui/loader/Loader';

import ModalContent from './ModalContent';
import ModalFooter from './ModalFooter';
import ModalHeader from './ModalHeader';

type SizeValue = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl';

function getSizeClass(size: SizeValue) {
  switch (size) {
    case 'sm':
      return 'max-w-sm';
    case 'md':
      return 'max-w-md';
    case 'lg':
      return 'max-w-lg';
    case 'xl':
      return 'max-w-xl';
    case '2xl':
      return 'max-w-2xl';
    case '3xl':
      return 'max-w-3xl';
    case '4xl':
      return 'max-w-4xl';
    case '5xl':
      return 'max-w-5xl';
    case '6xl':
      return 'max-w-6xl';
    case '7xl':
      return 'max-w-7xl';
    default:
      return 'max-w-lg';
  }
}

type Props = {
  id?: string;
  open: boolean;
  fullScreen?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl';
  dismissible?: boolean;
  loading?: boolean;
  elevated?: boolean;
  onClose?: () => void;
  children: React.ReactNode;
};

const getMaxSizeClassName = (size: string) => {
  switch (size) {
    case 'sm':
      return 'max-w-sm';
    case 'md':
      return 'max-w-md';
    case 'lg':
      return 'max-w-lg';
    case 'xl':
      return 'max-w-xl';
    case '2xl':
      return 'max-w-2xl';
    case '3xl':
      return 'max-w-3xl';
    case '4xl':
      return 'max-w-4xl';
    case '5xl':
      return 'max-w-5xl';
    case '6xl':
      return 'max-w-6xl';
    case '7xl':
      return 'max-w-7xl';
    default:
      return 'max-w-lg';
  }
};

function Modal({
  id,
  loading = false,
  size = 'lg',
  dismissible = false,
  elevated = false,
  open,
  onClose,
  children,
}: Props) {
  const keyListener = useRef<any>(null);

  const handleClose = () => {
    if (onClose) onClose();
  };

  const handleOverlayClick = () => {
    handleClose();
  };

  const listenToEscape = (e) => {
    if (e.keyCode === 27) {
      handleClose(); // close on escape
    }
  };

  const addListener = () => {
    if (!keyListener.current) {
      window.addEventListener('keydown', listenToEscape, false);
      keyListener.current = listenToEscape;
    }
  };

  const removeListener = () => {
    if (keyListener.current) {
      window.removeEventListener('keydown', keyListener.current, false);
      keyListener.current = null;
    }
  };

  useEffect(() => {
    if (open) {
      addListener();
    } else {
      removeListener();
    }

    return () => {
      removeListener();
    };
  }, [open]);

  const sizeClass = getSizeClass(size);

  return (
    <Overlay open={open} onOverlayClick={handleOverlayClick} elevated={elevated}>
      <Transition
        id={id}
        show={open}
        className={`inline-block transform overflow-visible rounded-lg bg-white text-left shadow-xl transition-all my-8 align-middle ${sizeClass} w-full`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-headline"
        enter="ease-out duration-300"
        enterFrom="opacity-0 translate-y-4 translate-y-0 scale-95"
        enterTo="opacity-100 translate-y-0 scale-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100 translate-y-0 scale-100"
        leaveTo="opacity-0 translate-y-4 translate-y-0 scale-95"
      >
        <Transition
          className="absolute inset-0 z-50"
          show={loading}
          enter="transition ease-in duration-50"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition ease-out duration-50"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white opacity-70 rounded-lg">
            <Loader size="huge" />
          </div>
        </Transition>

        <div onClick={(e) => e.stopPropagation()} className="contents">
          {children}
        </div>

        {dismissible && (
          <div className="absolute top-0 right-0 hidden pt-4 pr-4 block">
            <button
              type="button"
              className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              onClick={() => handleClose()}
            >
              <span className="sr-only">{gettext('Close')}</span>
              <Icon name="x" className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        )}
      </Transition>
    </Overlay>
  );
}

export default Object.assign(Modal, { Header: ModalHeader, Content: ModalContent, Footer: ModalFooter });
